<template>
  <div class="image-content">
    <SectionHeader title="图片内容" />

    <p class="image-content__desc">替换当前页插画，支持 JPG、PNG 格式</p>

    <div class="image-content__preview">
      <img v-if="imageUrl" :src="imageUrl" alt="插画预览" class="image-content__img" />
      <div v-else class="image-content__placeholder">
        <SvgIcon name="editor-image" :size="32" color="#CBD5E1" />
        <span>暂无图片</span>
      </div>

      <!-- 裁剪指示器 -->
      <div v-if="isCropped && imageUrl" class="image-content__crop-overlay">
        <div class="image-content__crop-mask" :class="`image-content__crop-mask--${crop}`"></div>
      </div>
    </div>

    <p v-if="isCropped && imageUrl" class="image-content__crop-hint">
      当前页面为跨页展示，预览呈现完整画面
    </p>

    <div class="image-content__actions">
      <button class="image-content__btn image-content__btn--primary" @click="$emit('ai-generate')">
        <SvgIcon name="editor-magic-wand" :size="16" />
        <span>AI 生成</span>
      </button>
      <button class="image-content__btn image-content__btn--default" @click="handleLocalUpload">
        <SvgIcon name="editor-upload" :size="12" color="#64748B" />
        <span>本地上传</span>
      </button>
    </div>

    <div class="image-content__divider"></div>

    <!-- 隐藏的文件选择器，用于本地上传替换图片 -->
    <input
      ref="fileInputRef"
      type="file"
      accept=".jpg,.jpeg,.png"
      class="image-content__file-input"
      @change="handleFileChange"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import SectionHeader from './SectionHeader.vue';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';
  import type { PageSide } from '@/types';

  const props = defineProps<{
    imageUrl?: string;
    crop?: PageSide;
  }>();

  const isCropped = computed(() => !!props.crop && props.crop !== 'none');

  const emit = defineEmits<{
    'ai-generate': [];
    'local-upload': [file: File];
    'update:imageUrl': [value: string];
  }>();

  const fileInputRef = ref<HTMLInputElement | null>(null);

  function handleLocalUpload() {
    fileInputRef.value?.click();
  }

  function handleFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    // 校验文件类型
    if (!file.type.match(/image\/(jpeg|png)/)) {
      console.error('不支持的图片格式，仅支持 JPG/PNG');
      return;
    }

    emit('local-upload', file);

    // 重置 input，以便能重复选择同一文件
    input.value = '';
  }
</script>

<style scoped>
  .image-content {
    width: 100%;
    box-sizing: border-box;
    padding: 16px 20px 0;
  }

  .image-content__desc {
    margin: 4px 4px 0;
    font-weight: 400;
    font-size: 11.5px;
    line-height: 18.69px;
    color: #94a3b8;
  }

  .image-content__preview {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 12px;
    width: 100%;
    aspect-ratio: 290 / 146;
    background-color: rgba(239, 240, 247, 0.5);
    border-radius: 16px;
    overflow: hidden;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .image-content__preview:hover {
    background-color: rgba(239, 240, 247, 0.8);
  }

  .image-content__img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .image-content__placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    color: #94a3b8;
    font-size: 13px;
  }

  .image-content__actions {
    display: flex;
    gap: 10px;
    margin-top: 12px;
  }

  .image-content__btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    flex: 1;
    height: 46px;
    font-size: 13px;
    font-weight: 500;
    line-height: 19.5px;
    border-radius: 10px;
    border: 1px solid;
    background-color: #fff;
    cursor: pointer;
    transition:
      background-color 0.2s,
      opacity 0.2s;
  }

  .image-content__btn:active {
    opacity: 0.8;
  }

  .image-content__btn--primary {
    color: #6033e1;
    border-color: #6033e1;
  }

  .image-content__btn--primary:hover {
    background-color: #f8f5ff;
  }

  .image-content__btn--default {
    color: #64748b;
    border-color: #eff0f7;
  }

  .image-content__btn--default:hover {
    background-color: #f8fafc;
  }

  .image-content__divider {
    height: 1px;
    background-color: #eee;
    margin-top: 16px;
  }

  .image-content__file-input {
    display: none;
  }

  /* ============================================================
     裁剪提示文字
  ============================================================ */

  .image-content__crop-hint {
    margin: 4px 0 0;
    font-weight: 400;
    font-size: 11px;
    line-height: 11px;
    letter-spacing: 1px;
    color: #999999;
    text-align: center;
  }

  /* ============================================================
     裁剪指示器
  ============================================================ */

  .image-content__crop-overlay {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 1;
  }

  .image-content__crop-mask {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 50%;
    background-color: rgba(0, 0, 0, 0.4);
  }

  .image-content__crop-mask--left {
    right: 0;
  }

  .image-content__crop-mask--right {
    left: 0;
  }

</style>
